// src/components/EducationSettingsManager.jsx
// 🎛️ 教育設定管理コンポーネント - ページ版

import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { EDUCATION_TYPES, SKILL_THRESHOLDS } from '../utils/educationUtils';

const EducationSettingsManager = () => {
  const [settings, setSettings] = useState({
    priorities: {
      maxObservationPerDay: 3,
      maxGuidedPracticePerDay: 2,
      minExperienceForInstruction: 3,
      targetUtilizationRate: 85
    },
    educationTypes: {
      [EDUCATION_TYPES.FIRST_OBSERVATION]: { weight: 1, maxDuration: 60 },
      [EDUCATION_TYPES.GUIDED_PRACTICE]: { weight: 3, maxDuration: 180 },
      [EDUCATION_TYPES.SUPERVISED_INDEPENDENCE]: { weight: 2, maxDuration: 120 },
      [EDUCATION_TYPES.FINAL_ASSESSMENT]: { weight: 4, maxDuration: 240 }
    },
    skillThresholds: {
      beginnerMax: SKILL_THRESHOLDS.BEGINNER_MAX,
      intermediateMax: SKILL_THRESHOLDS.INTERMEDIATE_MAX,
      advancedMin: SKILL_THRESHOLDS.ADVANCED_MIN,
      instructorMin: SKILL_THRESHOLDS.INSTRUCTOR_MIN
    },
    autoAssignment: {
      enableEducationPriority: true,
      preferExperienceBalance: true,
      allowJuniorPairs: false,
      educationOverStaffing: true
    }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'educationSettings'));
      
      if (settingsDoc.exists()) {
        setSettings(prevSettings => ({
          ...prevSettings,
          ...settingsDoc.data()
        }));
      }
    } catch (error) {
      console.error('教育設定読み込みエラー:', error);
      showNotification('設定の読み込みに失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'systemSettings', 'educationSettings'), {
        ...settings,
        updatedAt: new Date().toISOString(),
        updatedBy: 'system' // TODO: ユーザー情報を追加
      });
      
      showNotification('設定を保存しました', 'success');
    } catch (error) {
      console.error('教育設定保存エラー:', error);
      showNotification('設定の保存に失敗しました', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handlePriorityChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      priorities: {
        ...prev.priorities,
        [key]: parseInt(value) || 0
      }
    }));
  };

  const handleEducationTypeChange = (type, field, value) => {
    setSettings(prev => ({
      ...prev,
      educationTypes: {
        ...prev.educationTypes,
        [type]: {
          ...prev.educationTypes[type],
          [field]: parseInt(value) || 0
        }
      }
    }));
  };

  const handleSkillThresholdChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      skillThresholds: {
        ...prev.skillThresholds,
        [key]: parseInt(value) || 0
      }
    }));
  };

  const handleAutoAssignmentChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      autoAssignment: {
        ...prev.autoAssignment,
        [key]: value
      }
    }));
  };

  // ✅ ESLintエラー修正: confirm → window.confirm
  const resetToDefaults = () => {
    if (window.confirm('設定をデフォルト値にリセットしますか？')) {
      setSettings({
        priorities: {
          maxObservationPerDay: 3,
          maxGuidedPracticePerDay: 2,
          minExperienceForInstruction: 3,
          targetUtilizationRate: 85
        },
        educationTypes: {
          [EDUCATION_TYPES.FIRST_OBSERVATION]: { weight: 1, maxDuration: 60 },
          [EDUCATION_TYPES.GUIDED_PRACTICE]: { weight: 3, maxDuration: 180 },
          [EDUCATION_TYPES.SUPERVISED_INDEPENDENCE]: { weight: 2, maxDuration: 120 },
          [EDUCATION_TYPES.FINAL_ASSESSMENT]: { weight: 4, maxDuration: 240 }
        },
        skillThresholds: {
          beginnerMax: 3,
          intermediateMax: 5,
          advancedMin: 6,
          instructorMin: 7
        },
        autoAssignment: {
          enableEducationPriority: true,
          preferExperienceBalance: true,
          allowJuniorPairs: false,
          educationOverStaffing: true
        }
      });
      showNotification('デフォルト設定にリセットしました', 'info');
    }
  };

  const getEducationTypeLabel = (type) => {
    const labels = {
      [EDUCATION_TYPES.FIRST_OBSERVATION]: '初回見学',
      [EDUCATION_TYPES.GUIDED_PRACTICE]: '指導下実践',
      [EDUCATION_TYPES.SUPERVISED_INDEPENDENCE]: '監督下独立',
      [EDUCATION_TYPES.FINAL_ASSESSMENT]: '最終評価'
    };
    return labels[type] || type;
  };

  const styles = {
    container: {
      padding: '2rem',
      maxWidth: '1200px',
      margin: '0 auto',
      fontFamily: 'sans-serif',
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    },
    header: {
      marginBottom: '2rem',
      backgroundColor: 'white',
      padding: '2rem',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    title: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: '0 0 0.5rem 0'
    },
    subtitle: {
      fontSize: '1rem',
      color: '#6b7280',
      margin: 0
    },
    content: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    },
    footer: {
      padding: '1.5rem 2rem',
      borderTop: '1px solid #e2e8f0',
      display: 'flex',
      justifyContent: 'space-between',
      gap: '0.5rem',
      backgroundColor: '#f8fafc'
    },
    section: {
      padding: '2rem',
      borderBottom: '1px solid #e2e8f0'
    },
    sectionTitle: {
      fontSize: '1.25rem',
      fontWeight: 'bold',
      marginBottom: '1.5rem',
      color: '#374151'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem'
    },
    formGroup: {
      marginBottom: '1rem'
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      transition: 'border-color 0.2s'
    },
    checkbox: {
      marginRight: '0.5rem'
    },
    button: {
      padding: '0.75rem 1.5rem',
      borderRadius: '0.5rem',
      fontWeight: '500',
      cursor: 'pointer',
      fontSize: '0.9rem',
      border: 'none',
      transition: 'all 0.2s'
    },
    primaryButton: {
      backgroundColor: '#4F46E5',
      color: 'white'
    },
    dangerButton: {
      backgroundColor: '#ef4444',
      color: 'white'
    },
    notification: {
      position: 'fixed',
      top: '1rem',
      right: '1rem',
      padding: '1rem 1.5rem',
      borderRadius: '8px',
      color: 'white',
      fontWeight: '500',
      zIndex: 10004,
      maxWidth: '350px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    },
    successNotification: {
      backgroundColor: '#10b981'
    },
    errorNotification: {
      backgroundColor: '#ef4444'
    },
    infoNotification: {
      backgroundColor: '#3b82f6'
    },
    educationTypeCard: {
      padding: '1.5rem',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '400px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '2rem', 
              height: '2rem', 
              border: '3px solid #e5e7eb',
              borderTop: '3px solid #4F46E5',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <div>設定を読み込み中...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <h1 style={styles.title}>🎛️ 教育配置設定</h1>
        <p style={styles.subtitle}>
          教育重視人員配置システムの各種パラメータを設定できます
        </p>
      </div>

      <div style={styles.content}>
        {/* 基本優先度設定 */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>🎯 基本優先度設定</h3>
          <div style={styles.grid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>1日の最大見学配置数</label>
              <input
                type="number"
                style={styles.input}
                value={settings.priorities.maxObservationPerDay}
                onChange={(e) => handlePriorityChange('maxObservationPerDay', e.target.value)}
                min="0"
                max="10"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>1日の最大実践指導数</label>
              <input
                type="number"
                style={styles.input}
                value={settings.priorities.maxGuidedPracticePerDay}
                onChange={(e) => handlePriorityChange('maxGuidedPracticePerDay', e.target.value)}
                min="0"
                max="10"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>指導者の最低経験年数</label>
              <input
                type="number"
                style={styles.input}
                value={settings.priorities.minExperienceForInstruction}
                onChange={(e) => handlePriorityChange('minExperienceForInstruction', e.target.value)}
                min="1"
                max="10"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>目標活用率（%）</label>
              <input
                type="number"
                style={styles.input}
                value={settings.priorities.targetUtilizationRate}
                onChange={(e) => handlePriorityChange('targetUtilizationRate', e.target.value)}
                min="50"
                max="100"
              />
            </div>
          </div>
        </div>

        {/* 教育タイプ設定 */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📚 教育タイプ設定</h3>
          <div style={styles.grid}>
            {Object.entries(settings.educationTypes).map(([type, config]) => (
              <div key={type} style={styles.educationTypeCard}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#4F46E5' }}>
                  {getEducationTypeLabel(type)}
                </h4>
                <div style={styles.formGroup}>
                  <label style={styles.label}>優先度重み</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={config.weight}
                    onChange={(e) => handleEducationTypeChange(type, 'weight', e.target.value)}
                    min="1"
                    max="10"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>最大所要時間（分）</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={config.maxDuration}
                    onChange={(e) => handleEducationTypeChange(type, 'maxDuration', e.target.value)}
                    min="30"
                    max="480"
                    step="30"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* スキル閾値設定 */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📊 スキルレベル閾値</h3>
          <div style={styles.grid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>初心者の最大レベル</label>
              <input
                type="number"
                style={styles.input}
                value={settings.skillThresholds.beginnerMax}
                onChange={(e) => handleSkillThresholdChange('beginnerMax', e.target.value)}
                min="1"
                max="7"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>中級者の最大レベル</label>
              <input
                type="number"
                style={styles.input}
                value={settings.skillThresholds.intermediateMax}
                onChange={(e) => handleSkillThresholdChange('intermediateMax', e.target.value)}
                min="1"
                max="7"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>上級者の最小レベル</label>
              <input
                type="number"
                style={styles.input}
                value={settings.skillThresholds.advancedMin}
                onChange={(e) => handleSkillThresholdChange('advancedMin', e.target.value)}
                min="1"
                max="7"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>指導者の最小レベル</label>
              <input
                type="number"
                style={styles.input}
                value={settings.skillThresholds.instructorMin}
                onChange={(e) => handleSkillThresholdChange('instructorMin', e.target.value)}
                min="1"
                max="7"
              />
            </div>
          </div>
        </div>

        {/* 自動配置設定 */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>⚙️ 自動配置オプション</h3>
          <div style={styles.grid}>
            <div style={styles.formGroup}>
              <label style={{ ...styles.label, display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={settings.autoAssignment.enableEducationPriority}
                  onChange={(e) => handleAutoAssignmentChange('enableEducationPriority', e.target.checked)}
                />
                教育優先配置を有効にする
              </label>
            </div>
            <div style={styles.formGroup}>
              <label style={{ ...styles.label, display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={settings.autoAssignment.preferExperienceBalance}
                  onChange={(e) => handleAutoAssignmentChange('preferExperienceBalance', e.target.checked)}
                />
                経験年数のバランスを重視する
              </label>
            </div>
            <div style={styles.formGroup}>
              <label style={{ ...styles.label, display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={settings.autoAssignment.allowJuniorPairs}
                  onChange={(e) => handleAutoAssignmentChange('allowJuniorPairs', e.target.checked)}
                />
                若手同士のペアを許可する
              </label>
            </div>
            <div style={styles.formGroup}>
              <label style={{ ...styles.label, display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={settings.autoAssignment.educationOverStaffing}
                  onChange={(e) => handleAutoAssignmentChange('educationOverStaffing', e.target.checked)}
                />
                教育目的での人員過配置を許可する
              </label>
            </div>
          </div>
        </div>

        {/* フッター */}
        <div style={styles.footer}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              style={{ ...styles.button, ...styles.dangerButton }}
              onClick={resetToDefaults}
              disabled={saving}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
            >
              🔄 デフォルトに戻す
            </button>
          </div>
          
          <button
            style={{
              ...styles.button,
              ...styles.primaryButton,
              opacity: saving ? 0.6 : 1
            }}
            onClick={saveSettings}
            disabled={saving}
            onMouseEnter={(e) => !saving && (e.target.style.backgroundColor = '#4338ca')}
            onMouseLeave={(e) => !saving && (e.target.style.backgroundColor = '#4F46E5')}
          >
            {saving ? '💾 保存中...' : '💾 設定を保存'}
          </button>
        </div>
      </div>

      {/* 通知 */}
      {notification.show && (
        <div style={{
          ...styles.notification,
          ...(notification.type === 'success' ? styles.successNotification :
              notification.type === 'error' ? styles.errorNotification :
              styles.infoNotification)
        }}>
          {notification.message}
        </div>
      )}

      {/* CSS アニメーション */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        input:focus {
          outline: none;
          border-color: #4F46E5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
      `}</style>
    </div>
  );
};

export default EducationSettingsManager;